import {
    Dispatch,
    forwardRef,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react'
import style from './style.m.less'
import { useTranslation } from 'react-i18next'

import { Form, Input, Select, Spin, Typography, message } from 'antd'
import {
    ThermalCalcResultResponse,
    CalculatorDropdownData,
    CalcThermalReportRequest,
    CalculatorFormValues,
    FormHandle,
} from '../../types'
import { formatNumber, restrictInput } from '../../helpers'
import { ButtonsBlock } from '../index'
import request from '../../../../services/request'

const { Text } = Typography

interface IThermalForm {
    data: CalculatorDropdownData | null
    error: string
    resCalculate: ThermalCalcResultResponse
    // auditId: number
    // isLoadingAuditUpdate: boolean
    // resCalculatorAudit: CalculatorAuditResponse

    setError: Dispatch<SetStateAction<string>>
    scrollToMain: () => void
    fetchCalculate: (body: any) => void
    // resetAudit: () => void
    // auditUpdate: (auditData: CalculatorAuditValues) => void
    // updateAuditId: (values: any, onSuccess: (values: any, data: any) => void) => void
}

const ThermalForm = forwardRef<FormHandle, IThermalForm>(
    ({ data, error, resCalculate, setError, scrollToMain, fetchCalculate }, ref) => {
        const { t, i18n } = useTranslation()
        const locale = i18n.language.includes('ru') ? 'ru' : 'en'

        const [calcForm] = Form.useForm()

        const [currentRegionId, setCurrentRegionId] = useState<number>()
        const [allResults, setAllResults] = useState<{
            [gasTypeId: string]: { result: string; size: boolean }
        } | null>(null)

        useEffect(() => {
            if (resCalculate?.detail) {
                const newResults: typeof allResults = {}
                Object.entries(resCalculate.detail).forEach(([gasTypeId, vals]) => {
                    const gasIdNum = parseInt(gasTypeId)
                    newResults[gasTypeId] = {
                        result: formatResult(vals.result, gasIdNum),
                        size: typeof vals.result === 'number',
                    }
                })
                setAllResults(newResults)

                setTimeout(() => {
                    document
                        .getElementById('result_container')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 150)
            }
        }, [resCalculate])

        const formatResult = (value: number | string, gasType: number): string => {
            if (typeof value === 'number') {
                let formatValue
                if (gasType > 1 && gasType < 4) {
                    formatValue = value.toFixed(3).replace('.', ',')
                } else {
                    formatValue = value.toFixed(1).replace('.', ',')
                }
                return formatValue
            }

            return value as string
        }

        const onFinish = async (values: any) => {
            setError('')
            try {
                const requestData = {
                    volume: values.volume.replaceAll(' ', '').replace(',', '.'),
                    region_id: currentRegionId,
                    year_id: values.year,
                    locale: locale,
                }
                fetchCalculate(requestData)
            } catch (err) {
                setError(t('calculator.errors.calculation'))
                message.error(t('calculator.errors.calculation'))
            }
        }

        const resetForm = () => {
            calcForm.resetFields(['volume', 'region', 'year'])
            setAllResults(null)
            setCurrentRegionId(undefined)
            setTimeout(() => {
                scrollToMain()
            }, 200)
        }

        useImperativeHandle(ref, () => ({
            resetForm,
        }))

        const formatVolume = (value: string) => {
            if (!value || value === '') {
                return
            }
            if (value[value.length - 1] === '.' || value[value.length - 1] === ',') {
                return
            }

            const formatted = formatNumber(value)

            calcForm.setFieldsValue({ volume: formatted })
        }

        async function downloadReport() {
            const allValues = calcForm.getFieldsValue() as CalculatorFormValues
            const requestData: CalcThermalReportRequest = {
                volume: parseFloat(
                    (allValues.volume as string).replaceAll(' ', '').replace(',', '.'),
                ),
                region_id: parseInt(currentRegionId?.toString() as string),
                year_id: allValues.year as number,
                results: allResults
                    ? Object.entries(allResults).map(([gasTypeId, res]) => {
                          const gasType = data?.gas_types.find((g) => g.id.toString() === gasTypeId)
                          return {
                              gas_type_name: gasType ? gasType.name : `Газ ${gasTypeId}`,
                              result: res.size
                                  ? parseFloat(res.result.split(' ')[0].replace(',', '.'))
                                  : undefined,
                          }
                      })
                    : [],
            }
            const response = await request.post(
                '/api/extension/v1/files/calculator/thermal',
                requestData,
                { responseType: 'blob' },
            )
            const anchor = document.createElement('a')
            const disposition = response.headers['content-disposition']
            const filenameMatch = disposition?.match(/filename="?([^"]+)"?/)
            const filename = filenameMatch?.[1] || `thermal_report.pdf`
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const objectUrl = window.URL.createObjectURL(blob)

            anchor.href = objectUrl
            anchor.download = decodeURIComponent(escape(filename))
            anchor.click()
            window.URL.revokeObjectURL(objectUrl)
            document.body.removeChild(anchor)
        }

        if (!data) {
            return <Spin spinning tip={t('calculator.loading')} />
        }
        return (
            <>
                <Form
                    form={calcForm}
                    layout="vertical"
                    onFinish={onFinish}
                    className={style.formWrap}
                >
                    <div className={style.calculatorFields}>
                        <div className={style.formDescription}>
                            {t('calculator.thermalDescription')}
                        </div>
                        {/* Поле объема */}
                        <Form.Item
                            label={t('calculator.fields.thermalVolume.label')}
                            className={style.formItem}
                            name="volume"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.fields.thermalVolume.error.required'),
                                },
                                {
                                    validator: (_, value) => {
                                        if (!value) {
                                            return Promise.reject()
                                        }
                                        const normalizedValue = value
                                            .replaceAll(' ', '')
                                            .replace(',', '.')
                                        return /^\d+(\.\d{1,3})?$/.test(normalizedValue)
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                  t('calculator.fields.thermalVolume.error.format'),
                                              )
                                    },
                                },
                            ]}
                        >
                            <Input
                                onKeyDown={restrictInput}
                                onChange={(e) => formatVolume(e.target.value)}
                            />
                        </Form.Item>

                        {/* Поле региона */}
                        <Form.Item
                            className={style.formItem}
                            label={t('calculator.fields.region.label')}
                            name="region"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.fields.region.error'),
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                allowClear
                                onChange={(_, option) => {
                                    setCurrentRegionId((option as any).key)
                                }}
                                optionFilterProp="children"
                                optionLabelProp={'children'}
                            >
                                {data.regions.map((region: any) => (
                                    <Select.Option key={region.id} value={region.name}>
                                        {locale === 'ru' ? region.name : region.name_en}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {/* Календарный год */}
                        <Form.Item
                            className={style.formItem}
                            label={t('calculator.fields.year.label')}
                            name="year"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.fields.year.error'),
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="children"
                                optionLabelProp={'children'}
                            >
                                {data.years.map((year: any) => (
                                    <Select.Option key={year.id} value={year.id}>
                                        {year.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item>
                            <ButtonsBlock margin cancelAction={() => resetForm()} />
                        </Form.Item>
                    </div>
                    {error && <Text type="danger">{error}</Text>}
                </Form>
                {allResults && Object.keys(allResults).length > 0 && (
                    <div className={style.resultContainer} id="result_container">
                        <div className={style.resultHead}>
                            {t('calculator.results.header.part1')}
                            <strong>{t('calculator.results.header.boldThermal')}</strong>
                            {t('calculator.results.header.part2')}
                        </div>
                        <div className={style.resultText}>
                            {t('calculator.results.regionalMethod')}
                            <div className={style.resultLine}>
                                {(() => {
                                    const errorText = Object.values(allResults).find(
                                        (r) => !r.size,
                                    )?.result
                                    return errorText ? (
                                        <Text className={style.resultString}>{errorText}</Text>
                                    ) : null
                                })()}
                            </div>
                            {data?.gas_types.map((gasType) => {
                                const gasResult = allResults[String(gasType.id)]
                                if (!gasResult || !gasResult.size) {
                                    return null
                                }
                                const measure =
                                    gasType.id === data.gas_types[0].id
                                        ? t('calculator.results.measureShort')
                                        : t('calculator.results.measure')
                                return (
                                    <div key={gasType.id} className={style.resultLine}>
                                        {locale === 'ru'
                                            ? gasType.name
                                            : (gasType as any).name_en ?? gasType.name}{' '}
                                        <div className={style.resultWrap}>
                                            <Text className={style.resultNumeric}>
                                                {gasResult.result}
                                            </Text>
                                            <p className={style.measure}>{measure}</p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        <button onClick={downloadReport} className={style.primaryButton}>
                            {t('calculator.buttons.downloadReport')}
                        </button>
                    </div>
                )}
            </>
        )
    },
)
export default ThermalForm
